import { ROUTES } from "@app/constants";
import {
  BreifInfluencerV2StatusEnum,
  BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY,
} from "@entities/brief/lib/constants";
import { SelectContact } from "@entities/brief/ui/Influencer/v2/SelectContact";
import BriefNotifications from "@features/BriefNotifications/BriefNotifications";
import BulkReachout from "@features/BulkReachout";
import { OutreachTerms } from "@features/OutreachTerms";
import { SocialMediaInfluencerComp } from "@features/socialMediaInfluencer/SocialMediaInfluencerComp";
import { UnifiedCampaignSetup } from "@features/UnifiedCampaignSetup";
import { WhatsappLogoIcon } from "@phosphor-icons/react";
import { useTableRowDragSelection } from "@shared/lib/hooks";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Badge, Button, Input, Modal, Table } from "@shared/ui";
import { useBriefRowSelection } from "@widgets/CampaignBrief/ui/CampaignBriefTable/useBriefRowSelection";
import { PageHeader } from "@widgets/PageHeader";
import {
  ProjectWorkspaceTabs,
  type ProjectWorkspaceTab,
} from "@widgets/ProjectWorkspaceTabs";
import { Drawer, Dropdown, Flex } from "antd";
import { Footer } from "antd/es/layout/layout";
import classNames from "classnames";
import { ChevronDown, Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PRICING_TABS, usePricingProject } from "../hooks/usePricingProject";
import { usePricingProjectColumns } from "../hooks/usePricingProjectColumns";
import { CampaignProjectIdentity } from "./CampaignProjectIdentity/CampaignProjectIdentity";
import styles from "./PricingProject.module.scss";

export default function PricingProject() {
  const {
    queryClient,
    scrollRef,
    segmentedValue,
    setSegmentedValue,
    selectedInfluencer,
    setSelectedInfluencer,
    socialMediaInfluencer,
    setSocialMediaInfluencer,
    // deliverableModalProps,
    // setDeliverableModalProps,
    userId,
    briefId,
    brief,
    pricingPocFacets,
    searchQuery,
    setSearchQuery,
    contactsModalProps,
    setContactsModalProps,
    openPocForm,
    setOpenPocForm,
    briefInfluencers,
    pagination,
    isFetchingNextPage,
    isLoading,
    isPricingWorkloadLoading,
    influencerCampaignHistory,
    isFetchingInfluencerCampaignHistory,
    isFetchingUserDetails,
    filters,
    reachoutStatusFilter,
    setReachoutStatusFilter,
    contactFilter,
    setContactFilter,
    isContactFilterActive,
    deliverableFilter,
    setDeliverableFilter,
    sortBy,
    setSortBy,
    createdAtFilter,
    setCreatedAtFilter,
    updatedAtFilter,
    setUpdatedAtFilter,
    sortByCreatedAt,
    sortByUpdatedAt,
    setDateColumnSort,
    refetch,
    dynamicFields,
    setDynamicField,
    isShortlistedFilter,
    setIsShortlistedFilter,
  } = usePricingProject();

  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [uncheckList, setUncheckList] = useState<string[]>([]);
  const [workspaceTab, setWorkspaceTab] =
    useState<ProjectWorkspaceTab>("creators");

  const [reachOutModalProps, setReachOutModalProps] = useState<{
    visible: boolean;
    isResend: boolean;
    isSelectAll: boolean;
    ids: string[];
    notIncludeIds: string[];
    selectedCount: number;
  }>({
    visible: false,
    isResend: false,
    isSelectAll: false,
    ids: [],
    notIncludeIds: [],
    selectedCount: 0,
  });

  const totalCount = pagination?.totalCount ?? briefInfluencers?.length ?? 0;
  const reachoutCount = totalCount;

  const {
    checkedList,
    setCheckedList,
    selectedRows,
    setSelectedRows,
    getRowProps,
    clearSelection,
  } = useTableRowDragSelection(briefInfluencers ?? [], {
    scrollRootRef: scrollRef,
  });

  const hasSelectedRows = selectAll || checkedList.length > 0;

  const effectiveCount = selectAll
    ? Math.max(0, totalCount - uncheckList.length)
    : checkedList.length;

  useEffect(() => {
    setSelectAll(false);
    setUncheckList([]);
    setCheckedList([]);
    setSelectedRows([]);
    clearSelection();
  }, [
    segmentedValue,
    clearSelection,
    setCheckedList,
    setSelectedRows,
    workspaceTab,
  ]);

  useEffect(() => {
    setSelectedRows([]);
  }, [searchQuery, setSelectedRows]);

  const handleOpenReachOut = useCallback(
    (record: IBriefInfluencerV2, isResend: boolean) => {
      setReachOutModalProps({
        visible: true,
        isResend,
        isSelectAll: false,
        ids: [record._id],
        notIncludeIds: [],
        selectedCount: 1,
      });
    },
    []
  );

  const handleOpenBulkReachOut = useCallback(
    (isResend: boolean) => {
      if (hasSelectedRows) {
        setReachOutModalProps({
          visible: true,
          isResend,
          isSelectAll: selectAll,
          ids: selectAll ? [] : checkedList,
          notIncludeIds: selectAll ? uncheckList : [],
          selectedCount: effectiveCount,
        });
      } else {
        setReachOutModalProps({
          visible: true,
          isResend,
          isSelectAll: true,
          ids: [],
          notIncludeIds: [],
          selectedCount: reachoutCount,
        });
      }
    },
    [
      hasSelectedRows,
      selectAll,
      checkedList,
      uncheckList,
      effectiveCount,
      reachoutCount,
    ]
  );

  const handleOpenSelectedReachOut = useCallback(
    (isResend: boolean) => {
      setReachOutModalProps({
        visible: true,
        isResend,
        isSelectAll: selectAll,
        ids: selectAll ? [] : checkedList,
        notIncludeIds: selectAll ? uncheckList : [],
        selectedCount: effectiveCount,
      });
    },
    [selectAll, checkedList, uncheckList, effectiveCount]
  );

  const handleCloseReachOutModal = useCallback(() => {
    setReachOutModalProps({
      visible: false,
      isResend: false,
      isSelectAll: false,
      ids: [],
      notIncludeIds: [],
      selectedCount: 0,
    });
    setSelectAll(false);
    setUncheckList([]);
    setCheckedList([]);
    setSelectedRows([]);
    clearSelection();
  }, [clearSelection, setCheckedList, setSelectedRows]);

  const rowSelection = useBriefRowSelection({
    rows: briefInfluencers ?? [],
    totalCount: pagination?.totalCount || 0,
    selectAll,
    checkedList,
    uncheckList,
    setSelectAll,
    setCheckedList,
    setUncheckList,
    selectedRows,
    setSelectedRows,
    hasActiveSearch: Boolean(searchQuery?.trim()),
  });

  const columns = usePricingProjectColumns({
    briefId,
    userId,
    selectedInfluencer,
    setSelectedInfluencer,
    setSocialMediaInfluencer,
    // setDeliverableModalProps,
    setContactsModalProps,
    openPocForm,
    setOpenPocForm,
    influencerCampaignHistory,
    isFetchingInfluencerCampaignHistory,
    queryClient,
    reachoutStatusFilter,
    setReachoutStatusFilter,
    contactFilter,
    setContactFilter,
    isContactFilterActive,
    deliverableFilter,
    setDeliverableFilter,
    isShortlistedFilter,
    setIsShortlistedFilter,
    onOpenReachOut: handleOpenReachOut,
    sortBy,
    setSortBy,
    createdAtFilter,
    setCreatedAtFilter,
    updatedAtFilter,
    setUpdatedAtFilter,
    sortByCreatedAt,
    sortByUpdatedAt,
    setDateColumnSort,
    refetch,
    dynamicFields,
    setDynamicField,
    briefInfluencers: briefInfluencers ?? [],
  });

  const isDeclined = useCallback((record: IBriefInfluencerV2) => {
    return (
      record.isBrandDeclined ||
      record.isCreatorDeclined ||
      record.status === BreifInfluencerV2StatusEnum.Deleted
    );
  }, []);

  const handleOpenCampaign = () => {
    if (briefId) {
      window.open(ROUTES.BRIEF_V2.replace(":briefId", briefId));
    }
  };

  const actionMenuItems = useMemo(
    () => [
      {
        key: "send-reach-out",
        label: "Send Reach out",
        onClick: () => handleOpenBulkReachOut(false),
      },
      {
        key: "reach-out-again",
        label: "Reach Out again",
        onClick: () => handleOpenBulkReachOut(true),
      },
      {
        key: "open-campaign",
        label: "Open Campaign",
        onClick: handleOpenCampaign,
      },
    ],
    [handleOpenBulkReachOut]
  );

  return (
    <div className={styles.pricingPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <PageHeader
            title={brief?.name}
            backText="My Pricing"
            backTo={`${ROUTES.MY_PRICING}?userId=${userId}`}
          />
          {brief?.brand?.name && brief?.name ? (
            <CampaignProjectIdentity
              brand={brief.brand.name}
              logo={brief.brand.logoUrl}
              campaignTitle={brief.name}
              variant="page"
            />
          ) : null}
        </div>
        <div className={styles.headerRight}>
          <OutreachTerms terms={brief?.pricingOutreachTerms} />
        </div>
      </div>

      <ProjectWorkspaceTabs
        activeTab={workspaceTab}
        onTabChange={setWorkspaceTab}
      />

      {workspaceTab === "overview" ? (
        <UnifiedCampaignSetup
          showCampaignName={false}
          showOutreachTerms={false}
        />
      ) : (
        <section className={styles.contentSection}>
          <div>
            <div className={styles.tableWrapper} ref={scrollRef}>
              <Table
                rowKey="_id"
                columns={columns}
                title={() => (
                  <div className={styles.tableToolbar}>
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search creator handle, contact, or name"
                      allowClear
                      className={styles.tableSearch}
                      prefix={<Search size={14} />}
                    />
                    <div
                      className={styles.tabList}
                      role="tablist"
                      aria-label="Creator list view"
                    >
                      {PRICING_TABS.map((tab) => {
                        const isActive = segmentedValue === tab.value;
                        const count =
                          pricingPocFacets?.data?.myPricing?.[tab.countKey] ||
                          0;
                        return (
                          <button
                            key={tab.value}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""}`}
                            onClick={() => setSegmentedValue(tab.value)}
                          >
                            {tab.label}
                            <span
                              className={`${styles.tabCount} ${isActive ? styles.tabCountActive : ""}`}
                            >
                              {count}
                            </span>
                            {tab.value === "Negotiations Requested" &&
                            count > 0 ? (
                              <Badge
                                dot
                                offset={[-5, -5]}
                                className={styles.negoTabBadge}
                              />
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                    <Dropdown
                      trigger={["click"]}
                      placement="bottomRight"
                      menu={{ items: actionMenuItems }}
                    >
                      <Button
                        type="default"
                        className={styles.actionsButton}
                        onClick={(event) => event.stopPropagation()}
                      >
                        Actions
                        <ChevronDown size={14} />
                      </Button>
                    </Dropdown>
                  </div>
                )}
                dataSource={briefInfluencers}
                bordered
                loading={
                  isLoading || isPricingWorkloadLoading || isFetchingUserDetails
                }
                className={styles.pricingTable}
                // className={styles.briefsTable}
                scroll={{
                  x: "auto",
                  y:
                    selectedRows.length === 0
                      ? 630
                      : isFetchingNextPage
                        ? 560
                        : 590,
                }}
                pagination={false}
                virtual
                rowSelection={rowSelection}
                onRow={(record, index) => {
                  const actualIndex = (briefInfluencers ?? []).findIndex(
                    (inf) => inf._id === record._id
                  );
                  const rowIndex =
                    actualIndex >= 0 ? actualIndex : (index ?? 0);
                  return getRowProps(record, rowIndex);
                }}
                rowClassName={(record) => {
                  return classNames(styles.tableRow, {
                    [styles.rowDeclined]:
                      isDeclined(record) && !checkedList.includes(record._id),
                    [styles.rowPurchased]:
                      record.status === BreifInfluencerV2StatusEnum.Purchased &&
                      !checkedList.includes(record._id),
                  });
                }}
              />
              {isFetchingNextPage && (
                <Flex justify="center" align="center">
                  <div className={styles.loader}>
                    <Loader2 size={16} className={styles.loaderIcon} />
                    Loading more creators...
                  </div>
                </Flex>
              )}
            </div>
          </div>
        </section>
      )}

      {hasSelectedRows &&
        workspaceTab === "creators" &&
        createPortal(
          <Footer className={styles.selectionFooter}>
            <Flex
              justify="space-between"
              align="center"
              className={styles.selectionFooterContent}
            >
              <div>
                <b>{effectiveCount}</b> items selected
              </div>
              <Flex gap={10}>
                <Button
                  onClick={() => handleOpenSelectedReachOut(false)}
                  type="primary"
                  icon={<WhatsappLogoIcon size={16} />}
                >
                  Send Reach out
                </Button>
                <Button onClick={() => handleOpenSelectedReachOut(true)}>
                  Resend Reachout
                </Button>
              </Flex>
            </Flex>
          </Footer>,
          document.body
        )}

      {/* <Modal
        title={"View Deliverable"}
        open={
          deliverableModalProps.visible && deliverableModalProps.type === "view"
        }
        onCancel={() =>
          setDeliverableModalProps({
            visible: false,
            type: "create",
            deliverableId: "",
            briefInfluencerIds: [],
          })
        }
        footer={null}
        width={800}
        destroyOnClose
      >
        {deliverableModalProps.type === "view" && (
          <DeliverablePackageCard
            briefId={briefId || ""}
            defaultDeliverableId={
              selectedInfluencer?.deliverableV2?._id
            }
            briefInfluencerIds={deliverableModalProps.briefInfluencerIds}
            isEditable={false}
            showMarkDefaultButton
            onClose={() =>
              setDeliverableModalProps({
                visible: false,
                type: "create",
                deliverableId: "",
                briefInfluencerIds: [],
              })
            }
            deliverableId={deliverableModalProps.deliverableId || ""}
          />
        )}
      </Modal> */}

      <Drawer
        title="Influencer Details"
        open={socialMediaInfluencer ? true : false}
        width={1300}
        footer={null}
        destroyOnClose
        onClose={() => setSocialMediaInfluencer(null)}
      >
        {socialMediaInfluencer ? (
          <SocialMediaInfluencerComp
            creatorInfo={{
              name: socialMediaInfluencer?.influencerName,
              image: socialMediaInfluencer?.socialMedia?.profilePicUrl,
              username: socialMediaInfluencer?.socialMediaName,
            }}
            socialMediaInfluencerReq={
              socialMediaInfluencer?._id
                ? {
                    toOpenInfluencer: socialMediaInfluencer ? true : false,
                    influencerId: socialMediaInfluencer?._id,
                  }
                : {
                    username: {
                      platform:
                        socialMediaInfluencer?.socialMedia?.platform || "",
                      socialMediaUsername:
                        socialMediaInfluencer?.socialMedia?.username || "",
                    },
                    toOpenInfluencer: socialMediaInfluencer ? true : false,
                  }
            }
          />
        ) : null}
      </Drawer>

      <Modal
        title="Verify Contact"
        open={contactsModalProps.visible}
        onCancel={() =>
          setContactsModalProps({
            visible: false,
            briefInfluencerId: "",
            creatorInfo: undefined,
            conatcts: [],
            initialContact: null,
          })
        }
        footer={null}
        width={800}
        destroyOnClose
      >
        {contactsModalProps.visible && contactsModalProps.creatorInfo ? (
          <SelectContact
            initialContact={contactsModalProps.initialContact}
            briefInfluencerId={contactsModalProps.briefInfluencerId}
            onSuccess={() => {
              queryClient.invalidateQueries({
                queryKey: [...BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY],
              });
            }}
            onClose={() =>
              setContactsModalProps({
                visible: false,
                briefInfluencerId: "",
                creatorInfo: undefined,
                conatcts: [],
                addContactOnly: false,
                initialContact: null,
              })
            }
            creatorInfo={contactsModalProps.creatorInfo}
          />
        ) : null}
      </Modal>

      <Modal
        title={`${reachOutModalProps.isResend ? "Resend " : ""}${
          reachOutModalProps.selectedCount === 1
            ? "Reach-out"
            : "Bulk reach-out"
        }`}
        open={reachOutModalProps.visible}
        onCancel={handleCloseReachOutModal}
        footer={null}
        width={640}
        destroyOnClose
      >
        {reachOutModalProps.visible && briefId ? (
          <BulkReachout
            isResend={reachOutModalProps.isResend}
            filterParams={filters}
            briefId={briefId}
            isSelectAll={reachOutModalProps.isSelectAll}
            ids={reachOutModalProps.ids}
            notIncludeIds={reachOutModalProps.notIncludeIds}
            selectedCount={reachOutModalProps.selectedCount}
            onClose={handleCloseReachOutModal}
          />
        ) : null}
      </Modal>

      <BriefNotifications
        briefId={briefId || ""}
        selectedUserId={userId || ""}
        type="priceReachout"
      />
    </div>
  );
}
